import type { ForqSupabaseClient } from './client';
import {
  calculateServiceEstimateTotals,
  normalizePaperReturnInput,
  normalizePlanServiceJobInput,
  normalizeServiceSearchInput,
  priceServiceEstimateLines,
  validateServiceEstimateInput,
  type CaptureServicePaperReturnInput,
  type CreateServicePrintInput,
  type MarkServicePrintInput,
  type PlannableServiceOrder,
  type PlanServiceJobInput,
  type SaveServiceEstimateInput,
  type ServiceGateway,
  type ServiceJobRecord,
  type ServiceOverview,
  type ServicePage,
  type ServicePrintRecord,
  type ServiceSearchInput,
  type ServiceTechnicianOption,
  type SetServiceApprovalInput,
  type TransitionServiceJobInput,
  type UvvDueRecord,
} from '@forq/service-data';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

function object<T>(operation: string, value: unknown): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError(operation, 'The RPC returned an invalid object.');
  }
  return value as T;
}

function array<T>(operation: string, value: unknown): readonly T[] {
  if (!Array.isArray(value)) throw new SupabaseOperationError(operation, 'The RPC returned an invalid list.');
  return value as readonly T[];
}

export class SupabaseServiceGateway implements ServiceGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async getOverview(tenantId: string): Promise<ServiceOverview> {
    const { data, error } = await this.client.rpc('service_overview', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('service.overview', messageOf(error));
    return object<ServiceOverview>('service.overview', data);
  }

  async searchJobs(input: ServiceSearchInput): Promise<ServicePage> {
    const normalized = normalizeServiceSearchInput(input);
    const { data, error } = await this.client.rpc('service_search', {
      p_tenant_id: normalized.tenantId,
      p_query: normalized.query,
      p_bucket: normalized.bucket === 'all' ? null : normalized.bucket,
      p_execution_mode: normalized.executionMode === 'all' ? null : normalized.executionMode,
      p_kind: normalized.kind === 'all' ? null : normalized.kind,
      p_limit: normalized.pageSize,
      p_offset: (normalized.page - 1) * normalized.pageSize,
    });
    if (error) throw new SupabaseOperationError('service.search', messageOf(error));
    return object<ServicePage>('service.search', data);
  }

  async listPlannableOrders(tenantId: string): Promise<readonly PlannableServiceOrder[]> {
    const { data, error } = await this.client.rpc('service_plannable_orders', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('service.plannable', messageOf(error));
    return array<PlannableServiceOrder>('service.plannable', data ?? []);
  }

  async listUvvDue(tenantId: string): Promise<readonly UvvDueRecord[]> {
    const { data, error } = await this.client.rpc('machine_uvv_due', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('service.uvvDue', messageOf(error));
    return array<UvvDueRecord>('service.uvvDue', data ?? []);
  }

  async getJob(tenantId: string, serviceJobId: string): Promise<ServiceJobRecord> {
    const { data, error } = await this.client.rpc('service_get', {
      p_tenant_id: tenantId,
      p_service_job_id: serviceJobId,
    });
    if (error) throw new SupabaseOperationError('service.get', messageOf(error));
    return object<ServiceJobRecord>('service.get', data);
  }

  async listTechnicians(tenantId: string): Promise<readonly ServiceTechnicianOption[]> {
    const { data, error } = await this.client.rpc('service_technician_options', {
      p_tenant_id: tenantId,
    });
    if (error) throw new SupabaseOperationError('service.technicians', messageOf(error));
    return array<ServiceTechnicianOption>('service.technicians', data ?? []);
  }

  async planJob(rawInput: PlanServiceJobInput): Promise<ServiceJobRecord> {
    const input = normalizePlanServiceJobInput(rawInput);
    const { data, error } = await this.client.rpc('service_plan', {
      p_tenant_id: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('service.plan', messageOf(error));
    return object<ServiceJobRecord>('service.plan', data);
  }

  async createPrint(input: CreateServicePrintInput): Promise<ServicePrintRecord> {
    const { data, error } = await this.client.rpc('service_create_print', {
      p_tenant_id: input.tenantId,
      p_service_job_id: input.serviceJobId,
    });
    if (error) throw new SupabaseOperationError('service.print.create', messageOf(error));
    return object<ServicePrintRecord>('service.print.create', data);
  }

  async markPrintAsPrinted(input: MarkServicePrintInput): Promise<ServicePrintRecord> {
    const { data, error } = await this.client.rpc('service_mark_printed', {
      p_tenant_id: input.tenantId,
      p_service_job_id: input.serviceJobId,
      p_print_document_id: input.printDocumentId,
    });
    if (error) throw new SupabaseOperationError('service.print.mark', messageOf(error));
    return object<ServicePrintRecord>('service.print.mark', data);
  }

  async capturePaperReturn(rawInput: CaptureServicePaperReturnInput): Promise<ServiceJobRecord> {
    const input = normalizePaperReturnInput(rawInput);
    const { data, error } = await this.client.rpc('service_capture_paper_return', {
      p_tenant_id: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('service.paper-return', messageOf(error));
    return object<ServiceJobRecord>('service.paper-return', data);
  }

  async setApproval(input: SetServiceApprovalInput): Promise<ServiceJobRecord> {
    const { data, error } = await this.client.rpc('service_set_approval', {
      p_tenant_id: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('service.approval', messageOf(error));
    return object<ServiceJobRecord>('service.approval', data);
  }

  async saveEstimate(input: SaveServiceEstimateInput): Promise<ServiceJobRecord> {
    validateServiceEstimateInput(input);
    const lines = priceServiceEstimateLines(input.lines);
    const totals = calculateServiceEstimateTotals(lines);
    const { data, error } = await this.client.rpc('service_estimate_save', {
      p_tenant_id: input.tenantId,
      p_payload: { serviceJobId: input.serviceJobId, lines, ...totals, note: input.note?.trim() ?? '' },
    });
    if (error) throw new SupabaseOperationError('service.estimate', messageOf(error));
    return object<ServiceJobRecord>('service.estimate', data);
  }

  async transitionJob(input: TransitionServiceJobInput): Promise<ServiceJobRecord> {
    const { data, error } = await this.client.rpc('service_transition', {
      p_tenant_id: input.tenantId,
      p_service_job_id: input.serviceJobId,
      p_to_status: input.toStatus,
      p_reason: input.reason?.trim() ?? '',
    });
    if (error) throw new SupabaseOperationError('service.transition', messageOf(error));
    return object<ServiceJobRecord>('service.transition', data);
  }
}
